import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingState } from '@/components/ui/LoadingState';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { TIMING } from '@/constants/timing';
import { ResearchService, type DestinationWithCount, type VenueDetails, type ShortlistWithVenue, type SearchContext } from '@/services/ResearchService';
import { EventsService } from '@/services/EventsService';
import { ClientsService } from '@/services/ClientsService';
import { VenuesService } from '@/services/VenuesService';
import { VenueResearchService, type VenueResearch } from '@/services/VenueResearchService';
import { ResearchFormModal, type ResearchFormData } from '@/components/venues/ResearchFormModal';
import { Alert } from '@/components/ui/Alert/Alert';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DestinationTabs } from './DestinationTabs';
import { AddDestinationModal } from './AddDestinationModal';
import { EditDestinationModal } from './EditDestinationModal';
import { EditRegionModal } from './EditRegionModal';
import { EmptyDestinationsState } from './EmptyDestinationsState';
import { CompactSearchInterface } from './CompactSearchInterface';
import { EmptySearchState } from './EmptySearchState';
import { SearchResults } from './SearchResults';
import { Shortlist } from './Shortlist';
import { SearchResultsTable } from './SearchResultsTable';
import { ShortlistTable } from './ShortlistTable';
import { VenueDetailsModal } from './VenueDetailsModal';
import { RefineSearchModal } from './RefineSearchModal';
import { NoResultsState } from './NoResultsState';
import { EmptyShortlistState } from './EmptyShortlistState';
import { AddHotelModal } from './AddHotelModal';
import { Dialog } from '@/components/ui/Modal/Modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { ResearchControlPanel } from './ResearchControlPanel';
import { FilterSummaryBadge } from './FilterSummaryBadge';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { AIIcon } from '@/components/ui/AIIcon/AIIcon';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { expandDestinationsForSearch, recalculateShortlistCounts } from './utils/destinationHelpers';

interface ResearchStepProps {
  eventId: string;
  onContinueToRFPs?: () => void;
}

export function ResearchStep({ 
  eventId, 
  onContinueToRFPs,
}: ResearchStepProps) {
  const [destinations, setDestinations] = useState<DestinationWithCount[]>([]);
  const [activeDestinationId, setActiveDestinationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editDestinationId, setEditDestinationId] = useState<string | null>(null);
  const [editRegion, setEditRegion] = useState<DestinationWithCount | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{
    isOpen: boolean;
    destinationId: string | null;
    destinationName: string;
    loading: boolean;
    canDelete: boolean;
    shortlistCount: number;
    cventConnectedCount: number;
    approvedCount: number;
    isRegion: boolean;
    childCount: number;
    blockReasons: string[];
  }>({ isOpen: false, destinationId: null, destinationName: '', loading: false, canDelete: true, shortlistCount: 0, cventConnectedCount: 0, approvedCount: 0, isRegion: false, childCount: 0, blockReasons: [] });

  // Global search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalExtractedCriteria, setGlobalExtractedCriteria] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Per-destination results
  const [destinationResults, setDestinationResults] = useState<
    Record<string, {
      venues: VenueDetails[];
      unfilteredVenues: VenueDetails[];
      hasSearched: boolean;
      isAISearch: boolean; // Track if results are from AI search vs manual filtering
    }>
  >({});

  // Shortlist state
  const [shortlist, setShortlist] = useState<ShortlistWithVenue[]>([]);
  const [loadingShortlist, setLoadingShortlist] = useState(false);
  const [addingVenueId, setAddingVenueId] = useState<string | null>(null);
  const [removingShortlistId, setRemovingShortlistId] = useState<string | null>(null);

  // Modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<VenueDetails | null>(null);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [addHotelModalOpen, setAddHotelModalOpen] = useState(false);

  // Tab state for Search Results / Shortlist
  const [activeTab, setActiveTab] = useState<'search' | 'shortlist'>('search');

  // Context for search
  const [searchContext, setSearchContext] = useState<SearchContext | null>(null);
  
  // Event filters (original filters from event)
  const [eventFilters, setEventFilters] = useState<any>(null);

  // Enrichment state
  const [enrichingVenueIds, setEnrichingVenueIds] = useState<Set<string>>(new Set());

  // Query active research jobs (pending/processing) to check for duplicates
  const { data: activeResearchData } = useQuery({
    queryKey: ['venue-research-active'],
    queryFn: async () => {
      // Fetch all pending and processing jobs
      const [pendingResult, processingResult] = await Promise.all([
        VenueResearchService.listResearch(1, 100, 'pending'),
        VenueResearchService.listResearch(1, 100, 'processing'),
      ]);
      return {
        items: [...pendingResult.items, ...processingResult.items],
      };
    },
    refetchInterval: 5000, // Poll every 5 seconds for active jobs
    staleTime: 1000,
  });

  // Filter active research jobs that match current destinations
  const activeResearchJobs = React.useMemo(() => {
    if (!activeResearchData?.items || destinations.length === 0) return [];
    
    return activeResearchData.items.filter((job: VenueResearch) => {
      const jobCity = job.search_city?.toLowerCase().trim() || '';
      if (!jobCity) return false;
      
      // Extract job city name (first part before comma)
      const jobCityName = jobCity.split(',')[0].trim();
      const jobPlaceId = job.search_city_place_id;
      
      // Check if job matches any destination with precise matching
      return destinations.some(dest => {
        const destCity = dest.city?.toLowerCase().trim() || '';
        const destCountry = dest.country?.toLowerCase().trim() || '';
        const destPlaceId = dest.place_id;
        
        // Priority 1: Match by place_id if both have it (most precise)
        if (jobPlaceId && destPlaceId && jobPlaceId === destPlaceId) {
          return true;
        }
        
        // Priority 2: Match by exact city/country strings
        // For country destinations (city === country)
        if (dest.city === dest.country) {
          // Exact match: job city equals the country
          if (jobCity === destCountry) return true;
          // Format match: job city ends with ", Country"
          if (jobCity.endsWith(`, ${destCountry}`)) return true;
          return false;
        }
        
        // For city destinations, try exact matches
        const destFull = `${destCity}, ${destCountry}`.toLowerCase();
        
        // Exact match: full "city, country" format
        if (jobCity === destFull) return true;
        
        // Exact match: just city name (if it's unique)
        if (jobCity === destCity) return true;
        
        // Format match: job has format "city, country" and matches
        if (jobCityName === destCity && jobCity.includes(destCountry)) return true;
        
        return false;
      });
    });
  }, [activeResearchData?.items, destinations]);

  // Research modal mutation
  const createResearchMutation = useMutation({
    mutationFn: (data: ResearchFormData) => VenueResearchService.createResearch(data),
    onSuccess: (result) => {
      const jobsPlural = result.totalJobs === 1 ? 'job' : 'jobs';
      const message = result.totalJobs === 1
        ? `Research job started! The AI agent will discover hotels matching your criteria.`
        : `${result.totalJobs} research ${jobsPlural} started! The AI agent will discover hotels across all cities and searches.`;
      
      notificationStore.success(message, {
        title: 'Research Started',
      });
      setResearchModalOpen(false);
      // Invalidate research list to show the new jobs
      queryClient.invalidateQueries({ queryKey: ['venue-research'] });
      queryClient.invalidateQueries({ queryKey: ['venue-research-active'] });
    },
    onError: (error: Error) => {
      notificationStore.error(`Failed to start research: ${error.message}`, {
        title: 'Research Error',
      });
    },
  });

  // Build initial values for research form from current destinations and filters
  function buildResearchInitialValues(): Partial<ResearchFormData> {
    const initialValues: Partial<ResearchFormData> = {
      search_cities: destinations.map(dest => ({
        city: dest.city === dest.country ? dest.country : `${dest.city}, ${dest.country}`,
        place_id: dest.place_id || undefined,
        city_only: dest.city !== dest.country ? dest.city : undefined,
        location_type: dest.city === dest.country ? 'country' : 'city',
      })),
      search_sequences: 1,
    };

    // Pre-populate filters that can be directly set
    if (globalExtractedCriteria?.min_rooms) {
      initialValues.min_rooms = globalExtractedCriteria.min_rooms;
    }
    if (globalExtractedCriteria?.min_conference_capacity) {
      initialValues.min_conference_capacity = globalExtractedCriteria.min_conference_capacity;
    }
    if (globalExtractedCriteria?.min_star_rating) {
      initialValues.min_stars = globalExtractedCriteria.min_star_rating;
    }

    // Build additional instructions from filters that can't be directly set
    const additionalFilters: string[] = [];
    if (globalExtractedCriteria?.max_airport_distance_km) {
      additionalFilters.push(`Maximum airport distance: ${globalExtractedCriteria.max_airport_distance_km} km`);
    }
    if (globalExtractedCriteria?.has_spa) {
      additionalFilters.push('Must have spa facilities');
    }
    if (globalExtractedCriteria?.min_restaurants) {
      additionalFilters.push(`Minimum ${globalExtractedCriteria.min_restaurants} restaurant${globalExtractedCriteria.min_restaurants > 1 ? 's' : ''}`);
    }
    if (globalExtractedCriteria?.min_bars) {
      additionalFilters.push(`Minimum ${globalExtractedCriteria.min_bars} bar${globalExtractedCriteria.min_bars > 1 ? 's' : ''}`);
    }
    if (globalExtractedCriteria?.min_pools) {
      additionalFilters.push(`Minimum ${globalExtractedCriteria.min_pools} pool${globalExtractedCriteria.min_pools > 1 ? 's' : ''}`);
    }
    if (globalExtractedCriteria?.beach_access) {
      if (globalExtractedCriteria.beach_access === 'beachfront') {
        additionalFilters.push('Beachfront property');
      } else if (globalExtractedCriteria.beach_access === 'beach_access') {
        additionalFilters.push('Beach access available');
      }
    }
    if (globalExtractedCriteria?.all_inclusive) {
      additionalFilters.push('All-inclusive property');
    }
    if (globalExtractedCriteria?.property_type) {
      if (globalExtractedCriteria.property_type === 'adults_only') {
        additionalFilters.push('Adults-only property');
      } else if (globalExtractedCriteria.property_type === 'family_friendly') {
        additionalFilters.push('Family-friendly property');
      }
    }
    if (globalExtractedCriteria?.has_golf) {
      additionalFilters.push('Must have golf facilities');
    }
    if (globalExtractedCriteria?.min_tripadvisor_rating) {
      additionalFilters.push(`Minimum Tripadvisor rating: ${globalExtractedCriteria.min_tripadvisor_rating}`);
    }

    // Merge with existing additional instructions if any
    const existingInstructions = globalExtractedCriteria?.additional_instructions;
    if (additionalFilters.length > 0) {
      const newInstructions = additionalFilters.join('; ');
      if (existingInstructions) {
        initialValues.additional_instructions = `${existingInstructions}; ${newInstructions}`;
      } else {
        initialValues.additional_instructions = newInstructions;
      }
    } else if (existingInstructions) {
      initialValues.additional_instructions = existingInstructions;
    }

    return initialValues;
  }

  async function handleResearchSubmit(data: ResearchFormData) {
    await createResearchMutation.mutateAsync(data);
  }

  const researchService = new ResearchService();
  const eventsService = new EventsService();
  const clientsService = new ClientsService();
  const venuesService = new VenuesService();
  const notificationStore = useNotificationStore();
  const queryClient = useQueryClient();
  const { hasAnyPermission } = usePermissions();

  // Permission checks
  const canUpdate = hasAnyPermission([PERMISSIONS.UPDATE_VENUES, PERMISSIONS.MANAGE_VENUES]);

  useEffect(() => {
    loadDestinations();
  }, [eventId]);

  useEffect(() => {
    if (destinations.length > 0) {
      // Load search results for all destinations to show counts in tabs
      loadAllSearchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinations.length]); // Only re-run when count changes, not array reference

  useEffect(() => {
    if (activeDestinationId) {
      loadShortlist();
      loadLatestSearchResults();
    }
  }, [activeDestinationId]);

  // Load search context when destinations are loaded
  useEffect(() => {
    if (destinations.length > 0 && !searchContext) {
      loadSearchContext();
    }
  }, [destinations.length]);

  // Get current destination results for polling
  const currentDestResults = activeDestinationId ? destinationResults[activeDestinationId] : null;
  const currentVenues = currentDestResults?.venues || [];
  
  // Check if any venues in current search results have active jobs
  const hasProcessingVenues = currentVenues.some(
    v => v.enrichment_job_status === 'queued' || v.enrichment_job_status === 'processing' || enrichingVenueIds.has(v.id)
  );

  // Clean up enrichingVenueIds state when venues no longer have active jobs
  useEffect(() => {
    if (currentVenues.length === 0) return;
    
    setEnrichingVenueIds(prev => {
      const next = new Set(prev);
      let changed = false;

      // Remove venues that no longer have active jobs from the enriching set
      prev.forEach(venueId => {
        const venue = currentVenues.find(v => v.id === venueId);
        if (venue && !venue.enrichment_job_status) {
          next.delete(venueId);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [currentVenues]);

  // Poll for updates when venues have active enrichment jobs
  useEffect(() => {
    if (!hasProcessingVenues || !activeDestinationId) return;
    
    const interval = setInterval(() => {
      // Reload search results to get updated venue statuses
      loadLatestSearchResults();
    }, TIMING.POLLING.VENUE_ENRICHMENT);

    return () => clearInterval(interval);
  }, [hasProcessingVenues, activeDestinationId]);

  // Enrichment mutations
  const enrichMutation = useMutation({
    mutationFn: (venueId: string) => {
      // Add to enriching set immediately
      setEnrichingVenueIds(prev => new Set(prev).add(venueId));
      return venuesService.triggerEnrichment(venueId);
    },
    onSuccess: (_, venueId) => {
      notificationStore.success('Research Started', {
        title: 'AI Research Agent is now working on this venue',
      });
      // Reload search results to get updated venue data
      if (activeDestinationId) {
        loadLatestSearchResults();
      }
    },
    onError: (error, venueId) => {
      // Remove from enriching set on error
      setEnrichingVenueIds(prev => {
        const next = new Set(prev);
        next.delete(venueId);
        return next;
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger research';
      notificationStore.error(
        errorMessage === 'Enrichment already in progress for this venue' 
          ? 'Research is already in progress for this venue' 
          : errorMessage,
        { title: 'Research Failed' }
      );
    },
    onSettled: () => {
      // Refresh after a short delay to get updated status
      setTimeout(() => {
        if (activeDestinationId) {
          loadLatestSearchResults();
        }
      }, 1000);
    }
  });

  const resetMutation = useMutation({
    mutationFn: (venueId: string) => venuesService.resetEnrichment(venueId),
    onSuccess: () => {
      notificationStore.success('Research Reset', {
        title: 'Research status has been reset successfully',
      });
      // Reload search results to get updated venue data
      if (activeDestinationId) {
        loadLatestSearchResults();
      }
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset research';
      notificationStore.error(errorMessage, { title: 'Reset Failed' });
    }
  });

  // Bulk enrichment mutation
  const bulkEnrichMutation = useMutation({
    mutationFn: async (venueIds: string[]) => {
      // Add all venue IDs to enriching set immediately
      setEnrichingVenueIds(prev => {
        const next = new Set(prev);
        venueIds.forEach(id => next.add(id));
        return next;
      });
      
      // Use bulkAction to queue enrichment for all venues
      return await venuesService.bulkAction('enrich', {
        allFiltered: false,
        ids: venueIds,
      });
    },
    onSuccess: (result) => {
      notificationStore.success(`Queued ${result.processed} venue${result.processed === 1 ? '' : 's'} for research`, {
        title: 'Research Queued',
      });
      // Reload search results to get updated venue data
      if (activeDestinationId) {
        loadLatestSearchResults();
      }
    },
    onError: (error, venueIds) => {
      // Remove from enriching set on error
      setEnrichingVenueIds(prev => {
        const next = new Set(prev);
        venueIds.forEach(id => next.delete(id));
        return next;
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to queue bulk research';
      notificationStore.error(errorMessage, { title: 'Bulk Research Failed' });
    },
    onSettled: () => {
      // Refresh after a short delay to get updated status
      setTimeout(() => {
        if (activeDestinationId) {
          loadLatestSearchResults();
        }
      }, 1000);
    }
  });

  async function loadDestinations() {
    try {
      setLoading(true);
      const data = await researchService.listDestinations(eventId);
      setDestinations(data);
      
      // Set first destination as active if none selected
      if (data.length > 0 && !activeDestinationId) {
        setActiveDestinationId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load destinations:', error);
      notificationStore.error('Failed to load destinations', {
        title: 'Load Error',
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadSearchContext() {
    try {
      // Fetch fresh data to ensure we have the latest
      const event = await eventsService.get(eventId);
      const client = await clientsService.get(event.client_id);
      
      setSearchContext({
        client_name: client.name,
        event_name: event.name,
        event_notes: event.event_notes || null,
        attendee_count: null,
        destination: '', // Will be set per destination during search
      });
      
      // Load and store event filters
      if (event.venue_filters) {
        setEventFilters(event.venue_filters);
        
        // Pre-populate globalExtractedCriteria with event filters if no search has been done yet
        if (!globalExtractedCriteria) {
          setGlobalExtractedCriteria({
            min_rooms: event.venue_filters.min_rooms ?? null,
            min_conference_capacity: event.venue_filters.min_conference_capacity ?? null,
            min_star_rating: event.venue_filters.star_rating ?? null,
            max_airport_distance_km: event.venue_filters.max_airport_distance_km ?? null,
            has_spa: event.venue_filters.has_spa ?? null,
            min_restaurants: event.venue_filters.min_restaurants ?? null,
            min_bars: event.venue_filters.min_bars ?? null,
            min_pools: event.venue_filters.min_pools ?? null,
            beach_access: event.venue_filters.beach_access ?? null,
            all_inclusive: event.venue_filters.all_inclusive ?? null,
            property_type: event.venue_filters.property_type ?? null,
            has_golf: event.venue_filters.has_golf ?? null,
            min_tripadvisor_rating: event.venue_filters.min_tripadvisor_rating ?? null,
          });
        }
      } else {
        setEventFilters(null);
      }
    } catch (error) {
      console.error('Failed to load search context:', error);
    }
  }

  async function loadShortlist() {
    if (!activeDestinationId) return;

    try {
      setLoadingShortlist(true);

      // Server-side getShortlist already handles region aggregation
      // (returns items from region + all children)
      const shortlistData = await researchService.getShortlist(activeDestinationId);
      setShortlist(shortlistData);
      
      // Note: We don't recalculate counts here because:
      // 1. loadShortlist() only loads shortlist for active destination, not all destinations
      // 2. After shortlist changes, we refresh destinations from backend (which aggregates correctly)
      // 3. Backend is the single source of truth for shortlist counts
    } catch (error) {
      console.error('Failed to load shortlist:', error);
      notificationStore.error('Failed to load shortlist', {
        title: 'Load Error',
      });
    } finally {
      setLoadingShortlist(false);
    }
  }

  async function loadAllSearchResults() {
    if (destinations.length === 0) return;

    try {
      // Build flat list including children so they also get search results loaded
      const allDests: { id: string; city: string; parentRegionId?: string }[] = [];
      for (const dest of destinations) {
        allDests.push({ id: dest.id, city: dest.city });
        if (dest.destination_type === 'region' && dest.children?.length) {
          for (const child of dest.children) {
            allDests.push({ id: child.id, city: child.city, parentRegionId: dest.id });
          }
        }
      }

      // Load search results for all destinations in parallel
      const results = await Promise.all(
        allDests.map(async (dest) => {
          try {
            const result = await researchService.getLatestSearchResults(dest.id);
            return {
              destinationId: dest.id,
              parentRegionId: dest.parentRegionId,
              city: dest.city,
              result,
            };
          } catch (error) {
            console.error(`Failed to load search results for ${dest.city}:`, error);
            return {
              destinationId: dest.id,
              parentRegionId: dest.parentRegionId,
              city: dest.city,
              result: null,
            };
          }
        })
      );

      // Update destinationResults state with all results
      const newResults: Record<string, {
        venues: VenueDetails[];
        unfilteredVenues: VenueDetails[];
        hasSearched: boolean;
        isAISearch: boolean;
      }> = {};
      // Accumulate child results for region aggregation
      const regionAgg: Record<string, VenueDetails[]> = {};

      results.forEach(({ destinationId, parentRegionId, city, result }) => {
        if (result) {
          const taggedVenues = result.venues.map(v => ({ ...v, _sourceCity: city }));
          newResults[destinationId] = {
            venues: taggedVenues,
            unfilteredVenues: taggedVenues,
            hasSearched: true,
            isAISearch: !result.explicit_filters,
          };
          // Aggregate child results into parent region
          if (parentRegionId) {
            if (!regionAgg[parentRegionId]) regionAgg[parentRegionId] = [];
            regionAgg[parentRegionId].push(...taggedVenues);
          }
        }
      });

      // Build aggregated results for region parents (deduplicated by venue id:
      // overlapping child radius searches can return the same hotel from multiple
      // children — count and display each venue once).
      for (const [regionId, venues] of Object.entries(regionAgg)) {
        const seen = new Set<string>();
        const dedupedVenues: VenueDetails[] = [];
        for (const v of venues) {
          const key = String((v as any).id);
          if (seen.has(key)) continue;
          seen.add(key);
          dedupedVenues.push(v);
        }
        newResults[regionId] = {
          venues: dedupedVenues,
          unfilteredVenues: dedupedVenues,
          hasSearched: true,
          isAISearch: true,
        };
      }

      setDestinationResults(prev => ({
        ...prev,
        ...newResults,
      }));
    } catch (error) {
      console.error('Failed to load all search results:', error);
      // Don't show error - just silently fail
    }
  }

  async function loadLatestSearchResults() {
    if (!activeDestinationId) return;
    
    try {
      const result = await researchService.getLatestSearchResults(activeDestinationId);
      
      if (result) {
        // Populate state with persisted results (if not already loaded)
        setDestinationResults(prev => {
          // Only update if not already loaded (avoid overwriting with stale data)
          if (prev[activeDestinationId]?.hasSearched) {
            return prev;
          }
          
          return {
            ...prev,
            [activeDestinationId]: {
              venues: result.venues,
              unfilteredVenues: result.venues,
              hasSearched: true,
              isAISearch: !result.explicit_filters, // AI search if no explicit filters
            },
          };
        });
        
        // Only update global state if active destination and no current global state
        if (!globalExtractedCriteria) {
          setGlobalExtractedCriteria(result.criteria);
        }
        
        // Restore search query if available and not already set
        if (result.natural_language_query && !globalSearchQuery) {
          setGlobalSearchQuery(result.natural_language_query);
        }
      }
    } catch (error) {
      console.error('Failed to load latest search results:', error);
      // Don't show error - just silently fail (user can perform new search)
    }
  }

  async function handleAddDestination(destination: {
    city: string;
    country: string;
    region?: string;
    place_id?: string;
    location_json?: any;
    search_mode?: 'city_name' | 'radius';
    search_radius_km?: number;
  }) {
    try {
      const newDestination = await researchService.createDestination(eventId, destination);
      setDestinations(prev => [...prev, { ...newDestination, shortlist_count: 0 }]);
      setActiveDestinationId(newDestination.id);
      notificationStore.success(`Added ${destination.city} as a destination`, {
        title: 'Destination Added',
      });

      // Check if filters are applied and search the new destination if they are
      if (globalExtractedCriteria && searchContext) {
        // Check if any filters are actually set (not null)
        const hasActiveFilters = Object.values(globalExtractedCriteria).some(
          value => value !== null && value !== undefined
        );

        if (hasActiveFilters) {
          try {
            setIsSearching(true);
            
            // For countries (where city === country), use just the country name
            // For cities, use "City, Country" format so backend can distinguish
            const isCountry = newDestination.city === newDestination.country;
            const destinationName = isCountry 
              ? newDestination.country 
              : `${newDestination.city}, ${newDestination.country}`;
            
            const result = await researchService.searchVenues(
              newDestination.id,
              globalSearchQuery || 'all venues', // Use existing query or default
              {
                ...searchContext,
                destination: destinationName,
              },
              {
                min_rooms: globalExtractedCriteria.min_rooms ?? null,
                min_conference_capacity: globalExtractedCriteria.min_conference_capacity ?? null,
                star_rating: globalExtractedCriteria.min_star_rating ?? null,
                max_airport_distance_km: globalExtractedCriteria.max_airport_distance_km ?? null,
                // Amenities
                has_spa: globalExtractedCriteria.has_spa ?? null,
                min_restaurants: globalExtractedCriteria.min_restaurants ?? null,
                min_bars: globalExtractedCriteria.min_bars ?? null,
                min_pools: globalExtractedCriteria.min_pools ?? null,
                beach_access: globalExtractedCriteria.beach_access ?? null,
                all_inclusive: globalExtractedCriteria.all_inclusive ?? null,
                property_type: globalExtractedCriteria.property_type ?? null,
                has_golf: globalExtractedCriteria.has_golf ?? null,
                min_tripadvisor_rating: globalExtractedCriteria.min_tripadvisor_rating ?? null,
              }
            );

            // Update state with results for the new destination
            setDestinationResults(prev => ({
              ...prev,
              [newDestination.id]: {
                venues: result.venues,
                unfilteredVenues: result.venues,
                hasSearched: true,
                isAISearch: false, // Mark as filter search, not AI search
              },
            }));

            notificationStore.success(`Found ${result.venues.length} venues matching your filters for ${destinationName}`, {
              title: 'Search Complete',
            });
          } catch (error: any) {
            console.error('Auto-search failed for new destination:', error);
            // Don't show error notification - destination was added successfully
            // User can manually search if needed
          } finally {
            setIsSearching(false);
          }
        }
      }
    } catch (error: any) {
      // Re-throw to let modal handle the error
      throw error;
    }
  }

  async function handleAddRegion(input: import('@/services/ResearchService').CreateRegionInput) {
    try {
      const { region, children } = await researchService.createRegion(eventId, input);
      // Add the region as a destination with its children nested
      const regionWithCount: DestinationWithCount = {
        ...region,
        shortlist_count: 0,
        total_in_database: 0,
        children: children.map(c => ({ ...c, shortlist_count: 0, total_in_database: 0 })),
      };
      setDestinations(prev => [...prev, regionWithCount]);
      setActiveDestinationId(region.id);
      notificationStore.success(
        `Created region "${input.display_name}" with ${children.length} cities`,
        { title: 'Region Created' }
      );

      // Auto-search region cities if filters are active (same as handleAddDestination)
      if (globalExtractedCriteria && searchContext) {
        // Check if any filters are actually set (not null)
        const hasActiveFilters = Object.values(globalExtractedCriteria).some(
          value => value !== null && value !== undefined
        );

        if (hasActiveFilters && children.length > 0) {
          try {
            setIsSearching(true);
            
            // Use shared utility to expand destinations (ensures consistency)
            const searchableDestinations = expandDestinationsForSearch([regionWithCount]);
            
            // Search each child city in the region
            const searchPromises = searchableDestinations.map(async (dest) => {
              try {
                const isCountry = dest.city === dest.country;
                const destinationName = isCountry
                  ? dest.country
                  : `${dest.city}, ${dest.country}`;

                const result = await researchService.searchVenues(
                  dest.id,
                  globalSearchQuery || 'all venues',
                  {
                    ...searchContext,
                    destination: destinationName,
                  },
                  {
                    min_rooms: globalExtractedCriteria.min_rooms ?? null,
                    min_conference_capacity: globalExtractedCriteria.min_conference_capacity ?? null,
                    star_rating: globalExtractedCriteria.min_star_rating ?? null,
                    max_airport_distance_km: globalExtractedCriteria.max_airport_distance_km ?? null,
                    // Amenities
                    has_spa: globalExtractedCriteria.has_spa ?? null,
                    min_restaurants: globalExtractedCriteria.min_restaurants ?? null,
                    min_bars: globalExtractedCriteria.min_bars ?? null,
                    min_pools: globalExtractedCriteria.min_pools ?? null,
                    beach_access: globalExtractedCriteria.beach_access ?? null,
                    all_inclusive: globalExtractedCriteria.all_inclusive ?? null,
                    property_type: globalExtractedCriteria.property_type ?? null,
                    has_golf: globalExtractedCriteria.has_golf ?? null,
                    min_tripadvisor_rating: globalExtractedCriteria.min_tripadvisor_rating ?? null,
                  }
                );

                return {
                  destinationId: dest.id,
                  city: dest.city,
                  venues: result.venues,
                };
              } catch (error) {
                console.error(`Auto-search failed for ${dest.city}:`, error);
                return {
                  destinationId: dest.id,
                  city: dest.city,
                  venues: [],
                };
              }
            });

            const results = await Promise.all(searchPromises);

            // Aggregate results: store under each child city AND aggregate under region
            const newResults: Record<string, any> = {};
            const allRegionVenues: VenueDetails[] = [];

            results.forEach(({ destinationId, city, venues }) => {
              // Store results for each child city
              newResults[destinationId] = {
                venues: venues,
                unfilteredVenues: venues,
                hasSearched: true,
                isAISearch: false,
              };
              
              // Collect venues for region aggregation
              allRegionVenues.push(...venues);
            });

            // Store aggregated results under region ID
            newResults[region.id] = {
              venues: allRegionVenues,
              unfilteredVenues: allRegionVenues,
              hasSearched: true,
              isAISearch: false,
            };

            // Update state with all results
            setDestinationResults(prev => ({
              ...prev,
              ...newResults,
            }));

            const totalVenues = allRegionVenues.length;
            notificationStore.success(
              `Found ${totalVenues} venues matching your filters across ${children.length} cities in ${input.display_name}`,
              { title: 'Search Complete' }
            );
          } catch (error: any) {
            console.error('Auto-search failed for region:', error);
            // Don't show error notification - region was created successfully
            // User can manually search if needed
          } finally {
            setIsSearching(false);
          }
        }
      }
    } catch (error: any) {
      throw error;
    }
  }

  async function handleGlobalSearch(query: string) {
    if (!query.trim() || destinations.length === 0) return;
    
    if (!searchContext) {
      notificationStore.error('Search context not ready. Please try again.', {
        title: 'Not Ready',
      });
      return;
    }

    try {
      setIsSearching(true);
      setGlobalSearchQuery(query);
      
      // Build a flat list of searchable destinations (expand regions into children)
      // Use shared utility function to ensure consistency
      const searchableDestinations = expandDestinationsForSearch(destinations);

      // Execute search for ALL searchable destinations in parallel
      const searchPromises = searchableDestinations.map(async (dest) => {
        try {
          const isCountry = dest.city === dest.country;
          const destinationName = isCountry
            ? dest.country
            : `${dest.city}, ${dest.country}`;

          const result = await researchService.searchVenues(
            dest.id,
            query,
            {
              ...searchContext,
              destination: destinationName,
            }
          );

          return {
            destinationId: dest.id,
            parentRegionId: dest.parentRegionId,
            city: dest.city,
            venues: result.venues,
            criteria: result.criteria,
          };
        } catch (error) {
          console.error(`Search failed for ${dest.city}:`, error);
          return {
            destinationId: dest.id,
            parentRegionId: dest.parentRegionId,
            city: dest.city,
            venues: [],
            criteria: null,
          };
        }
      });

      const results = await Promise.all(searchPromises);

      // Update state with results for each destination
      // For child cities: store under their own ID AND aggregate under region
      const newResults: Record<string, any> = {};
      const regionAggregates: Record<string, { venues: VenueDetails[]; unfilteredVenues: VenueDetails[] }> = {};

      results.forEach(({ destinationId, parentRegionId, city, venues }) => {
        // Tag each venue with source city for badge display
        const taggedVenues = venues.map(v => ({ ...v, _sourceCity: city }));

        // Store results for this specific destination
        newResults[destinationId] = {
          venues: taggedVenues,
          unfilteredVenues: taggedVenues,
          hasSearched: true,
          isAISearch: true,
        };

        // Also aggregate into parent region
        if (parentRegionId) {
          if (!regionAggregates[parentRegionId]) {
            regionAggregates[parentRegionId] = { venues: [], unfilteredVenues: [] };
          }
          regionAggregates[parentRegionId].venues.push(...taggedVenues);
          regionAggregates[parentRegionId].unfilteredVenues.push(...taggedVenues);
        }
      });

      // Store aggregated region results
      for (const [regionId, agg] of Object.entries(regionAggregates)) {
        newResults[regionId] = {
          venues: agg.venues,
          unfilteredVenues: agg.unfilteredVenues,
          hasSearched: true,
          isAISearch: true,
        };
      }

      setDestinationResults(newResults);
      setGlobalExtractedCriteria(results[0]?.criteria || null);

      const totalVenues = results.reduce((sum, r) => sum + r.venues.length, 0);
      const searchedCount = searchableDestinations.length;
      notificationStore.success(`Found ${totalVenues} venues across ${searchedCount} destination${searchedCount > 1 ? 's' : ''}`, {
        title: 'Search Complete',
      });
    } catch (error: any) {
      console.error('Global search failed:', error);
      const errorMessage = error.message || 'Search failed. Please try again.';
      notificationStore.error(errorMessage, {
        title: 'Search Error',
      });
    } finally {
      setIsSearching(false);
    }
  }

  function handleClearSearch() {
    // Clear all search-related state
    setGlobalSearchQuery('');
    setGlobalExtractedCriteria(null);
    setDestinationResults({});
    
    notificationStore.success('Search cleared', {
      title: 'Cleared',
    });
  }

  async function handleRemoveFilter(filterKey: string) {
    if (!globalExtractedCriteria || !searchContext) {
      return;
    }

    // Map filter keys to criteria keys
    const keyMapping: Record<string, keyof typeof globalExtractedCriteria> = {
      'min_rooms': 'min_rooms',
      'min_conference_capacity': 'min_conference_capacity',
      'min_star_rating': 'min_star_rating',
      'max_airport_distance_km': 'max_airport_distance_km',
      'has_spa': 'has_spa',
      'min_restaurants': 'min_restaurants',
      'min_bars': 'min_bars',
      'min_pools': 'min_pools',
      'beach_access': 'beach_access',
      'all_inclusive': 'all_inclusive',
      'property_type': 'property_type',
      'has_golf': 'has_golf',
      'min_tripadvisor_rating': 'min_tripadvisor_rating',
    };

    const criteriaKey = keyMapping[filterKey];
    if (!criteriaKey) {
      return;
    }

    // Create updated criteria with the filter removed
    const updatedCriteria = {
      ...globalExtractedCriteria,
      [criteriaKey]: null,
    };

    // Convert to FilterValues format for handleApplyFilters
    const updatedFilters = {
      min_rooms: updatedCriteria.min_rooms ?? null,
      min_conference_capacity: updatedCriteria.min_conference_capacity ?? null,
      star_rating: updatedCriteria.min_star_rating ?? null,
      max_airport_distance_km: updatedCriteria.max_airport_distance_km ?? null,
      has_spa: updatedCriteria.has_spa ?? null,
      min_restaurants: updatedCriteria.min_restaurants ?? null,
      min_bars: updatedCriteria.min_bars ?? null,
      min_pools: updatedCriteria.min_pools ?? null,
      beach_access: updatedCriteria.beach_access ?? null,
      all_inclusive: updatedCriteria.all_inclusive ?? null,
      property_type: updatedCriteria.property_type ?? null,
      has_golf: updatedCriteria.has_golf ?? null,
      min_tripadvisor_rating: updatedCriteria.min_tripadvisor_rating ?? null,
    };

    // Apply the updated filters (this will refresh the search)
    await handleApplyFilters(updatedFilters);
  }

  async function handleAddToShortlist(venue: VenueDetails) {
    if (!activeDestinationId) return;

    try {
      setAddingVenueId(venue.id);
      await researchService.addToShortlist(activeDestinationId, {
        venue_id: venue.id,
        match_score: venue.match_score || undefined,
        match_reasoning: venue.match_reasoning || undefined,
      });
      
      // Reload shortlist
      await loadShortlist();
      
      // Refresh destinations from backend to get updated aggregated counts
      // Backend already aggregates shortlist_count from children for regions
      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);
      
      // Invalidate the shortlist query used by ResearchProposalTool to update accessible steps
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
      
      notificationStore.success('Added to shortlist', {
        title: 'Success',
      });
    } catch (error) {
      console.error('Failed to add to shortlist:', error);
      notificationStore.error('Failed to add to shortlist', {
        title: 'Error',
      });
    } finally {
      setAddingVenueId(null);
    }
  }

  // Handler for AddHotelModal — wraps handleAddToShortlist with a minimal venue object
  async function handleAddHotelToShortlist(venue: { id: string; property_name: string }) {
    if (!activeDestinationId) return;

    try {
      setAddingVenueId(venue.id);
      await researchService.addToShortlist(activeDestinationId, {
        venue_id: venue.id,
      });

      await loadShortlist();

      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);

      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });

      notificationStore.success(`Added ${venue.property_name} to shortlist`, {
        title: 'Success',
      });

      setActiveTab('shortlist');
    } catch (error: any) {
      console.error('Failed to add hotel to shortlist:', error);
      const message = error?.message?.includes('already')
        ? 'This hotel is already on your shortlist.'
        : 'Failed to add hotel to shortlist';
      notificationStore.error(message, { title: 'Error' });
    } finally {
      setAddingVenueId(null);
    }
  }

  async function handleRemoveFromShortlist(shortlistId: string) {
    if (!activeDestinationId) return;

    try {
      setRemovingShortlistId(shortlistId);
      await researchService.removeFromShortlist(shortlistId);
      
      // Reload shortlist
      await loadShortlist();
      
      // Refresh destinations from backend to get updated aggregated counts
      // Backend already aggregates shortlist_count from children for regions
      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);
      
      // Invalidate the shortlist query used by ResearchProposalTool to update accessible steps
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
      
      notificationStore.success('Removed from shortlist', {
        title: 'Success',
      });
    } catch (error) {
      console.error('Failed to remove from shortlist:', error);
      notificationStore.error('Failed to remove from shortlist', {
        title: 'Error',
      });
    } finally {
      setRemovingShortlistId(null);
    }
  }

  async function handleMoveVenue(shortlistId: string, destinationId: string) {
    try {
      await researchService.moveShortlistItem(shortlistId, destinationId);
      
      // Reload shortlist for current destination
      await loadShortlist();
      
      // Refresh destinations from backend to get updated aggregated counts
      // Backend already aggregates shortlist_count from children for regions
      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
      
      const targetDest = destinations.find(d => d.id === destinationId);
      notificationStore.success(`Venue moved to ${targetDest?.city || 'destination'}`, {
        title: 'Venue Moved',
      });
    } catch (error: any) {
      console.error('Failed to move venue:', error);
      notificationStore.error(error.message || 'Failed to move venue', {
        title: 'Error',
      });
      throw error;
    }
  }

  async function handleCreateDestinationAndMoveVenue(
    shortlistId: string,
    destination: { city: string; country: string; place_id?: string; destination_type?: 'city' | 'region' | 'country' }
  ) {
    try {
      // First create the destination — set display_name for region destinations
      const createInput = {
        ...destination,
        ...(destination.destination_type === 'region' ? { display_name: destination.city } : {}),
      };
      const newDestination = await researchService.createDestination(eventId, createInput);

      // Then move the venue
      await researchService.moveShortlistItem(shortlistId, newDestination.id);
      
      // Reload shortlist for current destination (which will recalculate counts automatically)
      await loadShortlist();
      
      // Refresh destinations to include the new one with correct counts
      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
      
      notificationStore.success(`Created ${destination.city} and moved venue`, {
        title: 'Destination Created',
      });
    } catch (error: any) {
      console.error('Failed to create destination and move venue:', error);
      notificationStore.error(error.message || 'Failed to create destination', {
        title: 'Error',
      });
      throw error;
    }
  }

  // Saves the hotel note shared with the RFP and Selection steps.
  async function handleUpdateNotes(shortlistId: string, notes: string, clientFacing: boolean) {
    try {
      await researchService.updateShortlistItem(shortlistId, { notes, notes_client_facing: clientFacing });
      
      // Update local state
      setShortlist(prev =>
        prev.map(item =>
          item.id === shortlistId ? { ...item, notes, notes_client_facing: clientFacing } : item
        )
      );
      
      notificationStore.success('Notes saved', {
        title: 'Success',
      });
    } catch (error) {
      console.error('Failed to update notes:', error);
      notificationStore.error('Failed to save notes', {
        title: 'Error',
      });
      throw error; // Keep the notes editor open with the unsaved draft
    }
  }

  async function handleClearAllShortlist() {
    if (!activeDestinationId) return;

    // Split items: removable vs Cvent-connected (protected)
    const removable = shortlist.filter(item => !item.cvent_supplier_id);
    const protected_ = shortlist.filter(item => !!item.cvent_supplier_id);

    if (removable.length === 0) {
      notificationStore.warning(
        `All ${protected_.length} venue${protected_.length > 1 ? 's are' : ' is'} connected to Cvent and cannot be removed. Disconnect them on the RFPs tab first.`,
        { title: 'Cannot Clear Shortlist' }
      );
      return;
    }

    try {
      await Promise.all(
        removable.map(item => researchService.removeFromShortlist(item.id))
      );

      // Reload
      await loadShortlist();

      // Update count
      const remainingCount = protected_.length;
      const updatedDestinations = destinations.map(d =>
        d.id === activeDestinationId ? { ...d, shortlist_count: remainingCount } : d
      );
      setDestinations(updatedDestinations);

      if (protected_.length > 0) {
        notificationStore.success(
          `Removed ${removable.length} venue${removable.length > 1 ? 's' : ''}. ${protected_.length} Cvent-connected venue${protected_.length > 1 ? 's were' : ' was'} kept.`,
          { title: 'Shortlist Partially Cleared' }
        );
      } else {
        notificationStore.success('Shortlist cleared', {
          title: 'Success',
        });
      }
    } catch (error) {
      console.error('Failed to clear shortlist:', error);
      notificationStore.error('Failed to clear shortlist', {
        title: 'Error',
      });
    }
  }

  function handleViewDetails(venue: VenueDetails) {
    setSelectedVenue(venue);
    setDetailsModalOpen(true);
  }

  async function handleApplyFilters(filters: any) {
    if (!searchContext) {
      notificationStore.error('Search context not loaded', { title: 'Error' });
      return;
    }

    try {
      setIsSearching(true);
      
      // Update extracted criteria to show applied filters
      setGlobalExtractedCriteria({
        min_rooms: filters.min_rooms ?? null,
        min_conference_capacity: filters.min_conference_capacity ?? null,
        min_star_rating: filters.star_rating ?? null,
        max_airport_distance_km: filters.max_airport_distance_km ?? null,
        // Amenities
        has_spa: filters.has_spa ?? null,
        min_restaurants: filters.min_restaurants ?? null,
        min_bars: filters.min_bars ?? null,
        min_pools: filters.min_pools ?? null,
        beach_access: filters.beach_access ?? null,
        all_inclusive: filters.all_inclusive ?? null,
        property_type: filters.property_type ?? null,
        has_golf: filters.has_golf ?? null,
        min_tripadvisor_rating: filters.min_tripadvisor_rating ?? null,
      });

      // Build a flat list of searchable destinations (expand regions into children)
      // Use shared utility function to ensure consistency
      const searchableDestinations = expandDestinationsForSearch(destinations);

      // Perform new backend search for each searchable destination with explicit filters
      const searchPromises = searchableDestinations.map(async (dest) => {
        // For countries (where city === country), use just the country name
        // For cities, use "City, Country" format so backend can distinguish
        const isCountry = dest.city === dest.country;
        const destinationName = isCountry 
          ? dest.country 
          : `${dest.city}, ${dest.country}`;
        
        const result = await researchService.searchVenues(
          dest.id,
          globalSearchQuery || 'all venues', // Use existing query or default
          {
            ...searchContext,
            destination: destinationName,
          },
          {
            min_rooms: filters.min_rooms ?? null,
            min_conference_capacity: filters.min_conference_capacity ?? null,
            star_rating: filters.star_rating ?? null,
            max_airport_distance_km: filters.max_airport_distance_km ?? null,
            // Amenities
            has_spa: filters.has_spa ?? null,
            min_restaurants: filters.min_restaurants ?? null,
            min_bars: filters.min_bars ?? null,
            min_pools: filters.min_pools ?? null,
            beach_access: filters.beach_access ?? null,
            all_inclusive: filters.all_inclusive ?? null,
            property_type: filters.property_type ?? null,
            has_golf: filters.has_golf ?? null,
            min_tripadvisor_rating: filters.min_tripadvisor_rating ?? null,
          }
        );
        return {
          destinationId: dest.id,
          parentRegionId: dest.parentRegionId,
          city: dest.city,
          venues: result.venues,
          criteria: result.criteria,
        };
      });

      const results = await Promise.all(searchPromises);

      // Update state with results for each destination
      // For child cities: store under their own ID AND aggregate under region
      const newResults: Record<string, any> = {};
      const regionAggregates: Record<string, { venues: VenueDetails[]; unfilteredVenues: VenueDetails[] }> = {};

      results.forEach(({ destinationId, parentRegionId, city, venues }) => {
        // Tag venues with source city for display
        const taggedVenues = venues.map(v => ({ ...v, _sourceCity: city }));
        
        // Store results for each child city
        newResults[destinationId] = {
          venues: taggedVenues,
          unfilteredVenues: taggedVenues,
          hasSearched: true,
          isAISearch: false, // Mark as filter search, not AI search
        };
        
        // Aggregate child results into parent region
        if (parentRegionId) {
          if (!regionAggregates[parentRegionId]) {
            regionAggregates[parentRegionId] = { venues: [], unfilteredVenues: [] };
          }
          regionAggregates[parentRegionId].venues.push(...taggedVenues);
          regionAggregates[parentRegionId].unfilteredVenues.push(...taggedVenues);
        }
      });

      // Store aggregated results for regions
      for (const [regionId, aggregate] of Object.entries(regionAggregates)) {
        newResults[regionId] = {
          venues: aggregate.venues,
          unfilteredVenues: aggregate.unfilteredVenues,
          hasSearched: true,
          isAISearch: false,
        };
      }

      setDestinationResults(prev => ({
        ...prev,
        ...newResults,
      }));
      setFiltersModalOpen(false);

      const totalVenues = results.reduce((sum, r) => sum + r.venues.length, 0);
      const totalDestinations = searchableDestinations.length;
      notificationStore.success(`Found ${totalVenues} venues matching your filters across ${totalDestinations} destination${totalDestinations > 1 ? 's' : ''}`, {
        title: 'Filters Applied',
      });
    } catch (error: any) {
      console.error('Filter search failed:', error);
      notificationStore.error(error.message || 'Filter search failed', {
        title: 'Search Error',
      });
    } finally {
      setIsSearching(false);
    }
  }

  function handleEditClick(destinationId: string) {
    setEditDestinationId(destinationId);
  }

  async function handleUpdateDestination(
    destinationId: string,
    updates: {
      search_mode: 'city_name' | 'radius';
      search_radius_km: number | null;
      location_json?: any;
      display_name?: string | null;
    },
    hotelsWereImported?: boolean
  ) {
    try {
      // Check if search settings changed (radius or search_mode)
      const destination = destinations.find(d => d.id === destinationId);
      const searchSettingsChanged = destination && (
        updates.search_mode !== destination.search_mode ||
        (updates.search_mode === 'radius' && updates.search_radius_km !== (destination.search_radius_km || 15))
      );
      
      // Check if there are existing search results for this destination
      const hasExistingResults = destinationResults[destinationId]?.hasSearched;
      
      await researchService.updateDestination(destinationId, updates);
      
      // Refresh destinations list
      const updatedDestinations = await researchService.listDestinations(eventId);
      setDestinations(updatedDestinations);
      
      // If search settings changed OR hotels were imported, and there are existing results, refresh them
      if ((searchSettingsChanged || hotelsWereImported) && hasExistingResults && searchContext) {
        const updatedDest = updatedDestinations.find(d => d.id === destinationId);
        if (updatedDest) {
          try {
            setIsSearching(true);
            
            // Re-run search with same query/criteria but updated settings
            const isCountry = updatedDest.city === updatedDest.country;
            const destinationName = isCountry 
              ? updatedDest.country 
              : `${updatedDest.city}, ${updatedDest.country}`;
            
            // Get query and criteria - try to load from persisted results if not in state
            let query = globalSearchQuery || 'all venues';
            let criteria = globalExtractedCriteria;
            
            // If we don't have criteria in state, try to load from persisted search results
            if (!criteria) {
              try {
                const persistedResult = await researchService.getLatestSearchResults(destinationId);
                if (persistedResult) {
                  query = persistedResult.natural_language_query || query;
                  criteria = persistedResult.criteria || {};
                }
              } catch (loadError) {
                console.error('Failed to load persisted search results:', loadError);
                // Continue with empty criteria
                criteria = {};
              }
            }
            
            const result = await researchService.searchVenues(
              destinationId,
              query,
              {
                ...searchContext,
                destination: destinationName,
              },
              {
                min_rooms: criteria?.min_rooms ?? null,
                min_conference_capacity: criteria?.min_conference_capacity ?? null,
                star_rating: criteria?.min_star_rating ?? null, // Map min_star_rating to star_rating
                max_airport_distance_km: criteria?.max_airport_distance_km ?? null,
                has_spa: criteria?.has_spa ?? null,
                min_restaurants: criteria?.min_restaurants ?? null,
                min_bars: criteria?.min_bars ?? null,
                min_pools: criteria?.min_pools ?? null,
                beach_access: criteria?.beach_access ?? null,
                all_inclusive: criteria?.all_inclusive ?? null,
                property_type: criteria?.property_type ?? null,
                has_golf: criteria?.has_golf ?? null,
                min_tripadvisor_rating: criteria?.min_tripadvisor_rating ?? null,
              }
            );
            
            // Check if this is a child city in a region (updatedDest from outer scope)
            const parentRegionId = updatedDest?.parent_destination_id;
            
            // Update search results with new venues
            setDestinationResults(prev => {
              const newResults = {
                ...prev,
                [destinationId]: {
                  venues: result.venues,
                  unfilteredVenues: result.venues,
                  hasSearched: true,
                  isAISearch: !result.explicit_filters,
                },
              };
              
              // If this is a child city, also update parent region aggregated results
              if (parentRegionId && prev[parentRegionId]) {
                // Find all child cities of this region
                const regionChildren = updatedDestinations.filter(d => d.parent_destination_id === parentRegionId);
                const allRegionVenues: VenueDetails[] = [];
                
                // Collect venues from all children (use updated result for current city, existing for others)
                regionChildren.forEach(child => {
                  if (child.id === destinationId) {
                    // Use the newly fetched results for this city
                    allRegionVenues.push(...result.venues.map(v => ({ ...v, _sourceCity: child.city })));
                  } else if (prev[child.id]?.venues) {
                    // Use existing results for other children
                    allRegionVenues.push(...prev[child.id].venues);
                  }
                });
                
                // Update region aggregated results
                newResults[parentRegionId] = {
                  venues: allRegionVenues,
                  unfilteredVenues: allRegionVenues,
                  hasSearched: true,
                  isAISearch: !result.explicit_filters,
                };
              }
              
              return newResults;
            });
            
            notificationStore.success(`Destination updated and search refreshed. Found ${result.venues.length} venues.`, {
              title: 'Success',
            });
          } catch (searchError: any) {
            console.error('Failed to refresh search after destination update:', searchError);
            // Still show success for the destination update, but note search refresh failed
            notificationStore.success('Destination updated. Please search again to see updated results.', {
              title: 'Success',
            });
          } finally {
            setIsSearching(false);
          }
        } else {
          notificationStore.success('Destination updated', {
            title: 'Success',
          });
        }
      } else {
        notificationStore.success('Destination updated', {
          title: 'Success',
        });
      }
    } catch (error: any) {
      console.error('Failed to update destination:', error);
      notificationStore.error('Failed to update destination', {
        title: 'Update Error',
      });
      throw error; // Re-throw so modal can handle it
    }
  }

  async function handleRemoveClick(destinationId: string) {
    const destination = destinations.find(d => d.id === destinationId);
    if (!destination) return;

    const name = destination.destination_type === 'region'
      ? (destination.display_name || destination.city)
      : `${destination.city}, ${destination.country}`;

    // Open dialog immediately in loading state
    setRemoveConfirm({
      isOpen: true,
      destinationId,
      destinationName: name,
      loading: true,
      canDelete: true,
      shortlistCount: 0,
      cventConnectedCount: 0,
      approvedCount: 0,
      isRegion: false,
      childCount: 0,
      blockReasons: [],
    });

    try {
      const preflight = await researchService.preflightDeleteDestination(destinationId);
      setRemoveConfirm(prev => ({
        ...prev,
        loading: false,
        ...preflight,
      }));
    } catch (error) {
      console.error('Preflight check failed:', error);
      setRemoveConfirm(prev => ({
        ...prev,
        loading: false,
        canDelete: false,
        blockReasons: ['Failed to check destination status. Please try again.'],
      }));
    }
  }

  async function handleRemoveConfirm() {
    if (!removeConfirm.destinationId) return;

    try {
      await researchService.deleteDestination(removeConfirm.destinationId);

      const newDestinations = destinations.filter(d => d.id !== removeConfirm.destinationId);
      setDestinations(newDestinations);

      // If we removed the active destination, select another
      if (activeDestinationId === removeConfirm.destinationId) {
        setActiveDestinationId(newDestinations.length > 0 ? newDestinations[0].id : null);
      }

      notificationStore.success('Destination removed', {
        title: 'Success',
      });
    } catch (error: any) {
      console.error('Failed to remove destination:', error);
      notificationStore.error(error?.message || 'Failed to remove destination', {
        title: 'Cannot Remove',
      });
    } finally {
      setRemoveConfirm({ isOpen: false, destinationId: null, destinationName: '', loading: false, canDelete: true, shortlistCount: 0, cventConnectedCount: 0, approvedCount: 0, isRegion: false, childCount: 0, blockReasons: [] });
    }
  }

  // Get results for active destination (calculate before early returns for hooks consistency)
  const activeDestResults = activeDestinationId ? destinationResults[activeDestinationId] : undefined;
  const searchResultsVenues = activeDestResults?.venues || [];

  // Calculate venues needing enrichment (not enriched and not already queued/processing)
  // Must be before any early returns to maintain hook order
  const venuesNeedingEnrichment = React.useMemo(() => {
    return searchResultsVenues.filter(venue => {
      // Exclude venues that are already enriched
      if (venue.enrichment_status === 'enriched' && venue.enrichment_completed_at) {
        return false;
      }
      // Exclude venues that already have active jobs
      if (venue.enrichment_job_status === 'queued' || venue.enrichment_job_status === 'processing') {
        return false;
      }
      // Exclude venues currently being enriched
      if (enrichingVenueIds.has(venue.id)) {
        return false;
      }
      return true;
    });
  }, [searchResultsVenues, enrichingVenueIds]);

  // Handler for bulk enrichment
  async function handleBulkEnrich() {
    if (venuesNeedingEnrichment.length === 0) return;
    
    const venueIds = venuesNeedingEnrichment.map(v => v.id);
    await bulkEnrichMutation.mutateAsync(venueIds);
  }

  // Search top-level AND children so clicking a child city works
  // Must be before ALL early returns to maintain consistent hook order
  const activeDestination = React.useMemo(() => {
    for (const d of destinations) {
      if (d.id === activeDestinationId) return d;
      if (d.children) {
        const child = d.children.find(c => c.id === activeDestinationId);
        if (child) return child;
      }
    }
    return undefined;
  }, [destinations, activeDestinationId]);
  const isActiveRegion = activeDestination?.destination_type === 'region';

  if (loading) {
    return <LoadingState />;
  }

  // No destinations yet
  if (destinations.length === 0) {
    return (
      <>
        <EmptyDestinationsState onAddDestination={() => setAddModalOpen(true)} />
        <AddDestinationModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onAdd={handleAddDestination}
          onAddRegion={handleAddRegion}
          eventId={eventId}
          currentCount={destinations.length}
          existingDestinations={destinations.map(d => ({ city: d.city, country: d.country }))}
        />
      </>
    );
  }

  // Has destinations - show tabs and research interface
  const shortlistedVenueIds = new Set(shortlist.map(item => item.venue_id));
  // Create a map of venue_id -> shortlist item for quick lookup
  const shortlistItemsMap = new Map(shortlist.map(item => [item.venue_id, item]));
  const hasSearched = activeDestResults?.hasSearched || false;

  // Get total shortlist count across all destinations
  const totalShortlistCount = destinations.reduce((sum, d) => sum + d.shortlist_count, 0);

  return (
    <>
      {/* Unified Research Control Panel */}
      <ResearchControlPanel
        destinations={destinations.map(d => ({
          ...d,
          result_count: destinationResults[d.id]?.venues.length || 0,
          children: d.children?.map(c => ({
            ...c,
            result_count: destinationResults[c.id]?.venues.length || 0,
          })),
        }))}
        activeDestinationId={activeDestinationId}
        onDestinationChange={setActiveDestinationId}
        onAddDestination={() => setAddModalOpen(true)}
        onEditDestination={handleEditClick}
        onRemoveDestination={handleRemoveClick}
        onEditRegion={(region) => setEditRegion(region)}
        extractedCriteria={globalExtractedCriteria}
        onOpenFilters={() => setFiltersModalOpen(true)}
        onRemoveFilter={handleRemoveFilter}
        isSearching={isSearching}
        shortlistCount={totalShortlistCount}
        onContinueToRFPs={onContinueToRFPs}
        onResearchMore={() => setResearchModalOpen(true)}
        hasActiveResearch={activeResearchJobs.length > 0}
      />

      {/* Active Research Jobs Notification */}
      {activeResearchJobs.length > 0 && (
        <div className="bg-info-50 border-b border-info-200 px-4 py-3 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-2 flex-1">
              <Loader2 className="w-4 h-4 animate-spin text-info-600" />
              <span className="text-sm text-info-900">
                {activeResearchJobs.length} AI research {activeResearchJobs.length === 1 ? 'job' : 'jobs'} in progress
              </span>
            </div>
            <Link
              to="/organization/venues/ai-research"
              className="text-sm font-medium text-info-700 hover:text-info-800 underline inline-flex items-center gap-1"
            >
              View Details
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </Link>
          </div>
        </div>
      )}

      {/* Main content area - tabs layout */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'search' | 'shortlist')}>
          <div className="px-6 pt-4 pb-2">
            <TabsList>
              <TabsTrigger value="search">
                Search Results ({searchResultsVenues.length})
              </TabsTrigger>
              <TabsTrigger value="shortlist">
                Shortlist ({shortlist.length})
              </TabsTrigger>
            </TabsList>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            <TabsContent value="search" className="mt-0">
              {hasSearched ? (
                <>
                  {/* Refresh Research Button (when venues still need enrichment) */}
                  {canUpdate && venuesNeedingEnrichment.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <SimpleTooltip
                        content={`Queue research for ${venuesNeedingEnrichment.length} venue${venuesNeedingEnrichment.length === 1 ? '' : 's'} that have not been researched yet`}
                        side="top"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkEnrich}
                          disabled={bulkEnrichMutation.isPending}
                          isLoading={bulkEnrichMutation.isPending}
                          className="gap-1.5"
                        >
                          <AIIcon className="w-4 h-4" />
                          <span>Refresh Research</span>
                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                            {venuesNeedingEnrichment.length}
                          </Badge>
                        </Button>
                      </SimpleTooltip>
                    </div>
                  )}
                  {searchResultsVenues.length === 0 ? (
                    <NoResultsState onAdjustFilters={() => setFiltersModalOpen(true)} />
                  ) : (
                    <SearchResultsTable
                      venues={searchResultsVenues}
                      shortlistedVenueIds={shortlistedVenueIds}
                      shortlistItems={shortlistItemsMap}
                      onAddToShortlist={handleAddToShortlist}
                      onViewDetails={handleViewDetails}
                      onUpdateNotes={handleUpdateNotes}
                      addingVenueId={addingVenueId}
                      showAIScores={activeDestResults?.isAISearch ?? false}
                      showCityBadge={isActiveRegion}
                      onRunResearch={(venue) => enrichMutation.mutate(venue.id)}
                      onResetResearch={(venue) => resetMutation.mutate(venue.id)}
                      enrichingVenueIds={enrichingVenueIds}
                      canRunResearch={canUpdate}
                      onBulkEnrich={canUpdate ? handleBulkEnrich : undefined}
                      venuesNeedingEnrichment={venuesNeedingEnrichment.length}
                      isBulkEnriching={bulkEnrichMutation.isPending}
                      totalInDestination={activeDestination?.total_in_database}
                      destinationName={activeDestination ? (isActiveRegion ? (activeDestination.display_name || activeDestination.city) : `${activeDestination.city}, ${activeDestination.country}`) : undefined}
                    />
                  )}
                </>
              ) : (
                <EmptySearchState />
              )}
            </TabsContent>
            
            <TabsContent value="shortlist" className="mt-0">
              {shortlist.length === 0 ? (
                <EmptyShortlistState onAddHotel={() => setAddHotelModalOpen(true)} />
              ) : (
                <ShortlistTable
                  items={shortlist}
                  onRemove={handleRemoveFromShortlist}
                  onUpdateNotes={handleUpdateNotes}
                  onViewDetails={handleViewDetails}
                  onClearAll={handleClearAllShortlist}
                  onContinue={onContinueToRFPs}
                  isLoading={loadingShortlist}
                  removingId={removingShortlistId}
                  destinations={destinations.map(d => ({ id: d.id, city: d.city, country: d.country }))}
                  currentDestinationId={activeDestinationId || undefined}
                  onMoveVenue={handleMoveVenue}
                  onCreateDestinationAndMove={handleCreateDestinationAndMoveVenue}
                  onAddHotel={() => setAddHotelModalOpen(true)}
                />
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Add Destination Modal */}
      <AddDestinationModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddDestination}
        onAddRegion={handleAddRegion}
        eventId={eventId}
        currentCount={destinations.length}
        existingDestinations={destinations.map(d => ({ city: d.city, country: d.country }))}
      />

      {/* Edit Destination Modal */}
      {editDestinationId && (() => {
        // Search top-level and children to find the destination
        let editDest: DestinationWithCount | undefined;
        for (const d of destinations) {
          if (d.id === editDestinationId) { editDest = d; break; }
          if (d.children) {
            const child = d.children.find(c => c.id === editDestinationId);
            if (child) { editDest = child; break; }
          }
        }
        return editDest ? (
          <EditDestinationModal
            isOpen={true}
            onClose={() => setEditDestinationId(null)}
            destination={editDest}
            onUpdate={handleUpdateDestination}
          />
        ) : null;
      })()}

      {/* Edit Region Modal */}
      {editRegion && (
        <EditRegionModal
          isOpen={!!editRegion}
          onClose={() => setEditRegion(null)}
          region={editRegion}
          eventId={eventId}
          onUpdate={() => {
            // Refetch all destinations to reflect hierarchy changes
            loadDestinations();
            setEditRegion(null);
          }}
          standaloneDestinations={destinations.filter(
            d => d.destination_type === 'city' && !d.parent_destination_id
          )}
        />
      )}

      {/* Remove Destination Confirmation */}
      <Dialog
        isOpen={removeConfirm.isOpen}
        onClose={() => setRemoveConfirm({ isOpen: false, destinationId: null, destinationName: '', loading: false, canDelete: true, shortlistCount: 0, cventConnectedCount: 0, approvedCount: 0, isRegion: false, childCount: 0, blockReasons: [] })}
        title={removeConfirm.loading ? 'Checking...' : removeConfirm.canDelete ? 'Remove Destination' : 'Cannot Remove Destination'}
        description={
          removeConfirm.loading
            ? 'Checking destination status...'
            : !removeConfirm.canDelete
              ? removeConfirm.blockReasons.join(' ')
              : removeConfirm.isRegion && removeConfirm.childCount > 0 && removeConfirm.shortlistCount > 0
                ? `Are you sure you want to remove ${removeConfirm.destinationName}? ${removeConfirm.childCount} child destination${removeConfirm.childCount > 1 ? 's' : ''} will become standalone. ${removeConfirm.shortlistCount} venue${removeConfirm.shortlistCount > 1 ? 's' : ''} will be removed from the shortlist.`
                : removeConfirm.isRegion && removeConfirm.childCount > 0
                  ? `Are you sure you want to remove ${removeConfirm.destinationName}? ${removeConfirm.childCount} child destination${removeConfirm.childCount > 1 ? 's' : ''} will become standalone.`
                  : removeConfirm.shortlistCount > 0
                    ? `Are you sure you want to remove ${removeConfirm.destinationName}? ${removeConfirm.shortlistCount} venue${removeConfirm.shortlistCount > 1 ? 's' : ''} will be removed from the shortlist.`
                    : `Are you sure you want to remove ${removeConfirm.destinationName}?`
        }
        onConfirm={removeConfirm.canDelete && !removeConfirm.loading ? handleRemoveConfirm : undefined}
        confirmText="Remove"
        confirmVariant="danger"
        isLoading={removeConfirm.loading}
      />

      {/* Venue Details Modal */}
      <VenueDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedVenue(null);
        }}
        venue={selectedVenue}
        isShortlisted={selectedVenue ? shortlistedVenueIds.has(selectedVenue.id) : false}
        onAddToShortlist={
          selectedVenue && !shortlistedVenueIds.has(selectedVenue.id)
            ? () => {
                handleAddToShortlist(selectedVenue);
                setDetailsModalOpen(false);
              }
            : undefined
        }
        isAddingToShortlist={selectedVenue?.id === addingVenueId}
      />

      {/* Refine Search Modal */}
      {activeDestination && (
        <RefineSearchModal
          isOpen={filtersModalOpen}
          onClose={() => setFiltersModalOpen(false)}
          destinationName="All Destinations"
          initialFilters={{
            min_rooms: globalExtractedCriteria?.min_rooms ?? null,
            min_conference_capacity: globalExtractedCriteria?.min_conference_capacity ?? null,
            star_rating: globalExtractedCriteria?.min_star_rating ?? null,
            max_airport_distance_km: globalExtractedCriteria?.max_airport_distance_km ?? null,
            // Amenities
            has_spa: globalExtractedCriteria?.has_spa ?? null,
            min_restaurants: globalExtractedCriteria?.min_restaurants ?? null,
            min_bars: globalExtractedCriteria?.min_bars ?? null,
            min_pools: globalExtractedCriteria?.min_pools ?? null,
            beach_access: globalExtractedCriteria?.beach_access ?? null,
            all_inclusive: globalExtractedCriteria?.all_inclusive ?? null,
            property_type: globalExtractedCriteria?.property_type ?? null,
            has_golf: globalExtractedCriteria?.has_golf ?? null,
            min_tripadvisor_rating: globalExtractedCriteria?.min_tripadvisor_rating ?? null,
          }}
          originalFilters={eventFilters ? {
            min_rooms: eventFilters.min_rooms ?? null,
            min_conference_capacity: eventFilters.min_conference_capacity ?? null,
            star_rating: eventFilters.star_rating ?? null,
            max_airport_distance_km: eventFilters.max_airport_distance_km ?? null,
            has_spa: eventFilters.has_spa ?? null,
            min_restaurants: eventFilters.min_restaurants ?? null,
            min_bars: eventFilters.min_bars ?? null,
            min_pools: eventFilters.min_pools ?? null,
            beach_access: eventFilters.beach_access ?? null,
            all_inclusive: eventFilters.all_inclusive ?? null,
            property_type: eventFilters.property_type ?? null,
            has_golf: eventFilters.has_golf ?? null,
            min_tripadvisor_rating: eventFilters.min_tripadvisor_rating ?? null,
          } : {}}
          onApply={handleApplyFilters}
          searchContext={searchContext}
          destinationId={activeDestination.id}
        />
      )}

      {/* Research More Venues Modal */}
      <ResearchFormModal
        isOpen={researchModalOpen}
        onClose={() => setResearchModalOpen(false)}
        onSubmit={handleResearchSubmit}
        isSubmitting={createResearchMutation.isPending}
        initialValues={buildResearchInitialValues()}
        activeResearchJobs={activeResearchJobs}
        destinations={destinations}
      />

      {/* Add Hotel Modal */}
      <AddHotelModal
        isOpen={addHotelModalOpen}
        onClose={() => setAddHotelModalOpen(false)}
        onHotelAdded={handleAddHotelToShortlist}
        shortlistedVenueIds={shortlistedVenueIds}
        isAtMaxShortlist={shortlist.length >= 20}
      />
    </>
  );
}

